import { useCallback, useState } from "react";
import { Link } from "react-router-dom";
import {
  Activity,
  AlertTriangle,
  Clock3,
  Coins,
  DollarSign,
  Receipt,
  RefreshCw,
  TrendingUp,
  Zap,
} from "lucide-react";
import { Topbar } from "@/components/Topbar";
import { Button } from "@/components/Button";
import { Select } from "@/components/Select";
import {
  ChartTypeMenu,
  useChartType,
  ChartType,
} from "@/components/ChartTypeMenu";
import { OverviewChart } from "@/components/OverviewChart";
import {
  AiNavigation,
  count,
  usd,
  LoadState,
  Metric,
  tableClass,
} from "@/components/ControlCenter";
import {
  AiReportFilters,
  billingLabel,
  latency,
  nullableUsd,
} from "@/components/AiReportFilters";
import {
  controlCenterService,
  ControlFilters,
  UsageReport,
} from "@/services/control-center.service";
import { useAdminResource } from "@/hooks/useAdminResource";

const chartTypes: ChartType[] = ["bar", "line", "area", "pie", "donut"];
const dimensions = {
  byCompany: "Company",
  byProvider: "Provider",
  byModel: "Model",
  byFeature: "Feature",
  byDay: "Day",
  byBillingMode: "Billing mode",
};
const metrics = {
  requests: "Requests",
  platformCostUsd: "Platform cost",
  credits: "Credits charged",
  failures: "Failures",
};
export default function AiUsagePage() {
  const [filters, setFilters] = useState<ControlFilters>({ period: "month" });
  const { data, loading, error, refresh } = useAdminResource(
    useCallback(() => controlCenterService.usage(filters), [filters]),
  );
  const [trend, setTrend] = useChartType("ai-volume", "area", chartTypes);
  const [providers, setProviders] = useChartType(
    "ai-providers",
    "donut",
    chartTypes,
  );
  const [metric, setMetric] = useState<keyof typeof metrics>("requests");
  const [dimension, setDimension] =
    useState<keyof typeof dimensions>("byCompany");
  const daily = data
    ? Array.from({ length: data.days }, (_, index) => {
        const date = new Date(data.from);
        date.setUTCDate(date.getUTCDate() + index);
        const label = date.toISOString().slice(0, 10);
        return {
          label,
          value: data.byDay.find((row) => row._id === label)?.[metric] ?? 0,
        };
      })
    : [];
  const requestLink = (extra: ControlFilters = {}) =>
    `/ai/requests?${new URLSearchParams(
      Object.entries({ ...filters, ...extra })
        .filter(([, value]) => value != null)
        .map(([key, value]) => [key, String(value)]),
    )}`;
  return (
    <>
      <Topbar
        title="AI usage & costs"
        breadcrumb="AI Control Center / Usage"
        right={
          <Button
            variant="ghost"
            size="sm"
            loading={loading}
            onClick={refresh}
            iconLeft={<RefreshCw size={14} />}
          >
            Refresh
          </Button>
        }
      />
      <div className="admin-page space-y-4">
        <AiNavigation />
        <AiReportFilters value={filters} onChange={setFilters} />
        <LoadState
          loading={loading}
          error={error}
          hasData={!!data}
          retry={refresh}
        />
        {data && (
          <>
            <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
              <Metric
                label="AI requests"
                value={count(data.totals.requests)}
                detail={`${count(data.totals.succeeded)} succeeded · ${count(data.totals.pending)} pending`}
                icon={Activity}
              />
              <Metric
                label="Tokens used"
                value={count(data.totals.tokensIn + data.totals.tokensOut)}
                detail={`${count(data.totals.tokensIn)} input · ${count(data.totals.tokensOut)} output`}
                icon={Zap}
                tone="info"
              />
              <Metric
                label="Platform provider cost"
                value={usd(data.totals.platformCostUsd)}
                detail="Recorded estimates · managed keys"
                icon={DollarSign}
                tone="warning"
              />
              <Metric
                label="Credits charged"
                value={count(data.totals.credits)}
                detail={`${count(data.totals.reservedCredits)} credits still reserved`}
                icon={Coins}
              />
              <Metric
                label="Billed credit value"
                value={nullableUsd(data.totals.customerChargeUsd)}
                detail="Consumed credit value in USD"
                icon={Receipt}
              />
              <Metric
                label="Gross margin estimate"
                value={nullableUsd(data.totals.grossMarginUsd)}
                detail={`${count(data.totals.financialRequests)} requests with charge records`}
                icon={TrendingUp}
              />
              <Metric
                label="Failed requests"
                value={count(data.totals.failures)}
                detail={`${count(data.totals.retries)} provider retries`}
                icon={AlertTriangle}
                tone={data.totals.failures ? "danger" : "primary"}
              />
              <Metric
                label="Average latency"
                value={latency(data.totals.averageLatencyMs)}
                detail="Completed request timing"
                icon={Clock3}
                tone="info"
              />
            </div>
            <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-text-muted">
              <p>
                Provider costs use saved rate cards. Credit value is usage
                value, not cash received. BYOK usage has no platform charge.
              </p>
              <Link className="font-medium text-primary" to={requestLink()}>
                View request logs →
              </Link>
            </div>
            {(data.totals.unpricedRequests > 0 ||
              data.totals.financialRequests < data.totals.requests) && (
              <div className="admin-panel flex gap-2 p-3 text-xs text-text-secondary">
                <AlertTriangle size={15} className="shrink-0 text-warning" />
                <span>
                  {count(data.totals.unpricedRequests)} requests have no cost
                  estimate. Charges and margins cover only{" "}
                  {count(data.totals.financialRequests)} of{" "}
                  {count(data.totals.requests)} requests; older, pending and
                  failed records may be incomplete.
                </span>
              </div>
            )}
            {data.totals.pending > 0 && (
              <Link
                to={requestLink({ status: "pending" })}
                className="flex items-center justify-between rounded-xl border border-warning/20 bg-warning-soft p-3 text-xs text-warning"
              >
                <span>
                  {count(data.totals.pending)} requests are pending. Review
                  older reservations before retrying.
                </span>
                <span>Review →</span>
              </Link>
            )}
            <div className="grid gap-4 lg:grid-cols-3">
              <section className="admin-panel min-w-0 p-4 lg:col-span-2">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <h2 className="text-sm font-semibold">Daily trend · UTC</h2>
                  <div className="flex items-center gap-2">
                    <Select
                      className="h-8 w-auto text-xs"
                      aria-label="Trend metric"
                      value={metric}
                      onChange={(event) =>
                        setMetric(event.target.value as keyof typeof metrics)
                      }
                    >
                      {Object.entries(metrics).map(([id, label]) => (
                        <option key={id} value={id}>
                          {label}
                        </option>
                      ))}
                    </Select>
                    <ChartTypeMenu
                      label="Daily AI usage"
                      value={trend}
                      onChange={setTrend}
                      allowed={chartTypes}
                    />
                  </div>
                </div>
                <div className="mt-4 h-60">
                  <OverviewChart
                    type={trend}
                    data={daily}
                    name={metrics[metric]}
                    formatValue={metric === "platformCostUsd" ? usd : count}
                    formatLabel={(value) =>
                      new Date(`${value}T00:00:00Z`).toLocaleDateString(
                        undefined,
                        { month: "short", day: "numeric", timeZone: "UTC" },
                      )
                    }
                  />
                </div>
              </section>
              <section className="admin-panel min-w-0 p-4">
                <div className="flex items-center justify-between gap-2">
                  <h2 className="text-sm font-semibold">
                    Requests by provider
                  </h2>
                  <ChartTypeMenu
                    label="AI providers"
                    value={providers}
                    onChange={setProviders}
                    allowed={chartTypes}
                  />
                </div>
                <div className="mt-4 h-44">
                  <OverviewChart
                    type={providers}
                    data={data.byProvider.map((row) => ({
                      label: row._id,
                      value: row.requests,
                    }))}
                    name="Requests"
                    center={
                      <strong className="text-xl">
                        {count(data.totals.requests)}
                      </strong>
                    }
                  />
                </div>
                <div className="mt-3 space-y-2">
                  {data.byProvider.map((row) => (
                    <div key={row._id} className="flex justify-between text-xs">
                      <span className="capitalize text-text-secondary">
                        {row._id}
                      </span>
                      <span>
                        {count(row.requests)}{" "}
                        <span className="ml-2 text-text-muted">
                          {usd(row.platformCostUsd)}
                        </span>
                      </span>
                    </div>
                  ))}
                </div>
              </section>
            </div>
            <UsageUnits data={data} />
            <section className="admin-panel overflow-hidden">
              <div className="flex flex-wrap items-center justify-between gap-2 p-4">
                <div>
                  <h2 className="text-sm font-semibold">Usage breakdown</h2>
                  <p className="mt-1 text-xs text-text-muted">
                    Top 30 companies, models and features by request volume.
                  </p>
                </div>
                <Select
                  className="w-auto"
                  aria-label="Breakdown dimension"
                  value={dimension}
                  onChange={(event) =>
                    setDimension(event.target.value as keyof typeof dimensions)
                  }
                >
                  {Object.entries(dimensions).map(([id, label]) => (
                    <option key={id} value={id}>
                      {label}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="overflow-x-auto">
                <table className={tableClass}>
                  <thead className="bg-surface-alt">
                    <tr>
                      <th>{dimensions[dimension]}</th>
                      <th>Requests</th>
                      <th>Input / output</th>
                      <th>Platform cost</th>
                      <th>Credits</th>
                      <th>Credit value</th>
                      <th>Margin</th>
                      <th>Failures</th>
                      <th>Latency</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data[dimension].map((row) => (
                      <tr key={row._id}>
                        <td>
                          {dimension === "byCompany" ? (
                            <Link
                              className="font-medium text-primary"
                              to={requestLink({ companyUid: row._id })}
                            >
                              {row.companyName}
                            </Link>
                          ) : dimension === "byBillingMode" ? (
                            billingLabel(row._id)
                          ) : (
                            row._id || "Unspecified"
                          )}
                        </td>
                        <td>{count(row.requests)}</td>
                        <td>
                          {count(row.tokensIn)} / {count(row.tokensOut)}
                        </td>
                        <td>{usd(row.platformCostUsd)}</td>
                        <td>{count(row.credits)}</td>
                        <td>{nullableUsd(row.customerChargeUsd)}</td>
                        <td>{nullableUsd(row.grossMarginUsd)}</td>
                        <td>{count(row.failures)}</td>
                        <td>{latency(row.averageLatencyMs)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {!data[dimension].length && (
                <p className="p-6 text-sm text-text-muted">
                  No usage recorded in this period.
                </p>
              )}
            </section>
          </>
        )}
      </div>
    </>
  );
}
function UsageUnits({ data }: { data: UsageReport }) {
  const t = data.totals;
  return (
    <section className="admin-panel p-4">
      <h2 className="text-sm font-semibold">Token & media detail</h2>
      <p className="mt-1 text-xs text-text-muted">
        Cache and reasoning counters are already included in input/output totals
        where reported by the provider.
      </p>
      <dl className="mt-4 grid grid-cols-2 gap-x-5 gap-y-3 sm:grid-cols-3 xl:grid-cols-6">
        {[
          ["Cached input", t.cachedInputTokens],
          ["Cache reads", t.cacheReadTokens],
          ["Cache writes", t.cacheWriteTokens],
          ["Reasoning", t.reasoningTokens],
          ["Images", t.imagesGenerated],
          ["Video seconds", t.videoSecondsGenerated],
          ["Audio input seconds", t.audioInputSeconds],
          ["Audio output seconds", t.audioOutputSeconds],
          ["Speech characters", t.characters],
        ].map(([label, value]) => (
          <div key={label}>
            <dt className="text-xs text-text-muted">{label}</dt>
            <dd className="mt-1 text-sm font-semibold tabular-nums">
              {count(Number(value))}
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );
}
